/**
 * GlobalFiltersPatchDataClient
 *
 * Data client for the PatchInput dropdown in Global Filters tab.
 * Handles on-demand loading with server-side type filtering.
 *
 * TWO RESPONSIBILITIES:
 *
 * 1. TYPE FILTERING
 *    Automatically injects the current department filter into every API request.
 *    - User is in "Sales" department → adds type='sale' to requests
 *    - API returns patches where type='sale' OR type='both'
 *    - User only sees relevant patches in dropdown
 *
 * 2. CACHE POPULATION
 *    Calls onFetch callback after each fetch to populate useCompanyPatchesCache.
 *    This enables patch lookups by ID for:
 *    - Displaying selected patch names in bulk edit banner
 *    - Checking patch compatibility when switching departments
 *    - Auto-switching department based on selected patch type
 *
 * FLOW:
 * PatchInput opens → request('index', {page:1}) → enriched to {page:1, type:'sale'}
 * → API returns patches → onFetch(patches) populates cache → patches shown in dropdown
 */

// Types
import type {
  TPatchDataClient,
  TPatchIndexPayload,
  TPaginatedPatches,
  TPatch,
} from '~/misc/patch/types';
import type { ESaleType } from '~/misc/listings/types';
import type {
  TPatchTypeFilter,
  TOnFetchCallback,
} from '~/modules/manager/company/global-filters/services/global-filters-patch-data-client/types';

// Services
import { AbstractDataClient } from '~/misc/services/data-client';
import { ManagerPatchUserResourceAdapter } from '~/modules/manager/models/patch/user-resource-adapter';

export class GlobalFiltersPatchDataClient
  extends AbstractDataClient<ManagerPatchUserResourceAdapter>
  implements TPatchDataClient
{
  private typeFilter: TPatchTypeFilter | undefined;
  private onFetch: TOnFetchCallback | undefined;

  constructor(companyId: number, onFetch?: TOnFetchCallback) {
    super(new ManagerPatchUserResourceAdapter(companyId));
    this.onFetch = onFetch;
  }

  // Converts ESaleType ('S'/'L') to patch type filter ('sale'/'let')
  setTypeFilter(saleType: ESaleType): void {
    this.typeFilter = saleType === 'S' ? 'sale' : 'let';
  }

  public async request<Type>(
    action: keyof ManagerPatchUserResourceAdapter,
    ...args: unknown[]
  ): Promise<Type> {
    if (action === 'index') {
      const payload: TPatchIndexPayload = (args[0] as TPatchIndexPayload) || {};

      // Enrich payload with current type filter
      const enrichedPayload: TPatchIndexPayload = {
        ...payload,
        type: this.typeFilter,
      };

      const result: TPaginatedPatches = await this.abortController.process(
        async (signal: AbortSignal) => await this.model.index(enrichedPayload, { signal })
      );

      // Populate the cache with fetched patches
      this.onFetch?.(result.data);

      return result as Type;
    }

    return super.request(action, ...args);
  }

  async fetchByIds(ids: (string | number)[]): Promise<TPatch[]> {
    if (ids.length === 0) return [];

    const numericIds: number[] = ids.map((id: string | number) => Number(id));
    const result: TPaginatedPatches = await this.model.index({ patches: numericIds });

    this.onFetch?.(result.data);

    return result.data;
  }
}
